import { CardKind, isExtraDeckCard } from '../types/cards';
import { ActionType, PlayerAction } from '../types/actions';
import {
  CardInstance,
  CardPosition,
  EnginePhase,
  MatchState,
  Seat,
  isOpeningTurn,
  otherSeat,
} from '../types/state';
import {
  CardLibrary,
  firstFreeIndex,
  getCardData,
  getLevel,
  effectiveStatBonus,
  getModifiedAtk,
  getModifiedDef,
  getMonsters,
  getSpellTraps,
  kindOf,
  requiredTributes,
  canIgniteUltimateOffering,
  canNormalOrTributeFromHand,
  soulExchangeTributeUids,
} from './helpers';
import { GameEngine } from './GameEngine';
import { extraFusionsByAtk, flipTargetUid, heavyStormWorthIt } from './aiEval';
import { forecastCombat, knownCardId, shouldMonsterBeInAttack } from './aiCombat';
import { plannerDifficulty } from './aiDeck';
import { EXODIA_PIECE_SET } from './aiRecipes';
import {
  cannotNormalSummon,
  COCOON_OF_EVOLUTION,
  evolveTribute,
  faceUpPetitMoths,
  GATE_GUARDIAN,
  gateTributeSet,
  getEquipConfig,
  GREAT_MOTH,
  LARVAE_MOTH,
  attackLockCode,
  magnetismTargets,
  mothEvolveTurn,
  SHIELD_AND_SWORD,
} from './effects';

const ADVANCE: PlayerAction = { type: ActionType.ADVANCE_PHASE };
const POLYMERIZATION = '24094653';
const FISSURE = '66788016';
const SOUL_EXCHANGE = '68005187';
const FLUTE = '43973174';
const GHOUL = '82542267';
const DE_SPELL = '19159413';
const REBORN = '83764718';
const OFFERING = 80604091;
const MAX_FUSION_TARGETS = 4;
const MAX_REBORN_TARGETS = 6;

export function cloneMatchState(state: MatchState): MatchState {
  return structuredClone(state);
}

/**
 * Applies `action` on a cloned engine. Opponent trap windows are passed:
 * the search never reads Set ids. Illegal actions return null.
 */
export function tryApplyAction(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  action: PlayerAction,
): MatchState | null {
  const engine = new GameEngine(cloneMatchState(state), library);
  try {
    engine.applyAction(seat, action);
  } catch {
    return null;
  }
  resolveOpponentWindows(engine, seat);
  return engine.getState();
}

/**
 * When the opponent would get a trap window, pass. Assuming they flip a
 * specific Set would require reading its id.
 */
function resolveOpponentWindows(engine: GameEngine, actingSeat: Seat): void {
  for (let i = 0; i < 16; i++) {
    const state = engine.getState();
    const order = state.pendingChainOrder;
    if (order) {
      if (order.chooser === actingSeat) return;
      try {
        engine.applyAction(order.chooser, {
          type: ActionType.ORDER_CHAIN,
          requestId: order.requestId,
          cardUids: order.cards.map((card) => card.uid),
        });
      } catch {
        return;
      }
      continue;
    }
    const targets = state.pendingTargets;
    if (targets && targets.chooser !== actingSeat) {
      const pick = targets.candidateUids.slice(0, Math.max(targets.min, 0));
      try {
        engine.applyAction(targets.chooser, {
          type: ActionType.SELECT_TARGETS,
          requestId: targets.requestId,
          targetUids: pick,
        });
      } catch {
        return;
      }
      continue;
    }
    const pending = state.pendingPriority;
    if (!pending || pending.responder === actingSeat) return;
    try {
      engine.applyAction(pending.responder, {
        type: ActionType.RESPOND_PRIORITY,
        requestId: pending.requestId,
        cardUid: null,
      });
    } catch {
      return;
    }
  }
}

/**
 * Candidate actions for the current phase. The engine still validates each one
 * via tryApplyAction — this list is only a generator, not a rules oracle.
 */
export function enumerateAiActions(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): PlayerAction[] {
  if (state.turnPlayer !== seat || state.status !== 'active') return [ADVANCE];
  switch (state.phase) {
    case EnginePhase.MAIN_1:
    case EnginePhase.MAIN_2:
      return enumerateMain(state, library, seat);
    case EnginePhase.BATTLE:
      return enumerateBattle(state, seat);
    default:
      return [ADVANCE];
  }
}

function enumerateMain(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): PlayerAction[] {
  const z = state.zones[seat];
  const actions: PlayerAction[] = [];
  const monsterFree = firstFreeIndex(z.monsters);
  const stFree = firstFreeIndex(z.spellTraps);

  pushPositionPlays(actions, state, library, seat);
  if (!z.hasNormalSummoned || z.extraNormalSummons > 0) {
    pushNormalSummons(actions, state, library, seat, monsterFree);
    pushTributeSummons(actions, state, library, seat, monsterFree);
  }
  pushGateGuardian(actions, state, seat);
  pushCocoon(actions, state, seat);
  pushMothEvolve(actions, state, seat);
  pushMonsterIgnitions(actions, state, library, seat);
  pushHandSpellTraps(actions, state, library, seat, stFree);
  pushSetActivations(actions, state, library, seat, z);
  actions.push(ADVANCE);
  return actions;
}

function pushPositionPlays(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): void {
  for (const m of getMonsters(state.zones[seat])) {
    if (!m.faceUp && !m.summonedThisTurn) {
      const targetUid = flipTargetUid(state, library, seat, m.cardId);
      actions.push({
        type: ActionType.FLIP_SUMMON,
        cardUid: m.uid,
        ...(targetUid ? { targetUid } : {}),
      });
    }
    if (
      m.faceUp &&
      !m.summonedThisTurn &&
      !m.positionChangedThisTurn &&
      !m.attackedThisTurn &&
      !m.declaredAttackThisTurn
    ) {
      actions.push({ type: ActionType.CHANGE_POSITION, cardUid: m.uid });
    }
  }
}

function pushNormalSummons(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  monsterFree: number,
): void {
  if (monsterFree === -1) return;
  for (const c of state.zones[seat].hand) {
    if (kindOf(c, library) !== CardKind.MONSTER) continue;
    if (isExtraDeckCard(getCardData(library, c.cardId))) continue;
    if (cannotNormalSummon(c.cardId)) continue;
    if (getLevel(c, library) > 4) continue;
    actions.push(
      { type: ActionType.NORMAL_SUMMON, cardUid: c.uid, zoneIndex: monsterFree },
      { type: ActionType.SET_MONSTER, cardUid: c.uid, zoneIndex: monsterFree },
    );
  }
}

function pushTributeSummons(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  monsterFree: number,
): void {
  const zoneIndex = Math.max(monsterFree, 0);
  for (const c of state.zones[seat].hand) {
    if (kindOf(c, library) !== CardKind.MONSTER) continue;
    if (isExtraDeckCard(getCardData(library, c.cardId))) continue;
    if (cannotNormalSummon(c.cardId)) continue;
    const need = requiredTributes(getLevel(c, library));
    if (need === 0) continue;
    const tributes = tributeFodderOptions(state, library, seat, need);
    for (const tributeUids of tributes) {
      actions.push(
        {
          type: ActionType.TRIBUTE_SUMMON,
          cardUid: c.uid,
          zoneIndex,
          tributeUids,
          position: CardPosition.ATTACK,
          faceDown: false,
        },
        {
          type: ActionType.TRIBUTE_SUMMON,
          cardUid: c.uid,
          zoneIndex,
          tributeUids,
          position: CardPosition.DEFENSE,
          faceDown: true,
        },
      );
    }
  }
}

function pushCocoon(actions: PlayerAction[], state: MatchState, seat: Seat): void {
  const card = state.zones[seat].hand.find((entry) => Number(entry.cardId) === COCOON_OF_EVOLUTION);
  if (!card) return;
  if (firstFreeIndex(state.zones[seat].spellTraps) === -1) return;
  const hosts = faceUpPetitMoths(state);
  const host = hosts.find((monster) => monster.owner === seat) ?? hosts[0];
  if (!host) return;
  actions.push({
    type: ActionType.EQUIP_FROM_HAND,
    cardUid: card.uid,
    targetUid: host.uid,
  });
}

function pushMothEvolve(actions: PlayerAction[], state: MatchState, seat: Seat): void {
  for (const code of [GREAT_MOTH, LARVAE_MOTH]) {
    const card = state.zones[seat].hand.find((entry) => Number(entry.cardId) === code);
    const turn = mothEvolveTurn(code);
    if (!card || turn === null) continue;
    const tribute = evolveTribute(state, seat, turn);
    if (!tribute) continue;
    actions.push({
      type: ActionType.SPECIAL_SUMMON_PROC,
      cardUid: card.uid,
      position: CardPosition.ATTACK,
      tributeUids: [tribute.moth.uid],
    });
  }
}

function pushGateGuardian(actions: PlayerAction[], state: MatchState, seat: Seat): void {
  const card = state.zones[seat].hand.find((entry) => Number(entry.cardId) === GATE_GUARDIAN);
  if (!card) return;
  const tributeUids = gateTributeSet(getMonsters(state.zones[seat]));
  if (!tributeUids) return;
  actions.push({
    type: ActionType.SPECIAL_SUMMON_PROC,
    cardUid: card.uid,
    position: CardPosition.ATTACK,
    tributeUids,
  });
}

function pushHandSpellTraps(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  stFree: number,
): void {
  for (const c of state.zones[seat].hand) {
    const kind = kindOf(c, library);
    if (kind === CardKind.SPELL) {
      pushSpellActivations(actions, state, library, seat, c.uid, c.cardId, true);
    }
    if (kind !== CardKind.SPELL && kind !== CardKind.TRAP) continue;
    const isField = getCardData(library, c.cardId).race === 'Field';
    if (!isField && stFree === -1) continue;
    actions.push({
      type: ActionType.SET_SPELL_TRAP,
      cardUid: c.uid,
      zoneIndex: Math.max(isField ? 0 : stFree, 0),
    });
  }
}

function pushSetActivations(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  z: MatchState['zones'][Seat],
): void {
  for (const c of getSpellTraps(z)) {
    if (c.faceUp) {
      if (
        Number(c.cardId) === OFFERING &&
        z.extraNormalSummons === 0 &&
        canIgniteUltimateOffering(
          state.turnPlayer,
          state.phase,
          seat,
          state.battleStep,
        ) &&
        canNormalOrTributeFromHand(state, seat, library) &&
        offeringWorthPaying(state, library, seat)
      ) {
        actions.push({ type: ActionType.ACTIVATE_TRAP, cardUid: c.uid });
      }
      continue;
    }
    if (kindOf(c, library) === CardKind.SPELL) {
      pushSpellActivations(actions, state, library, seat, c.uid, c.cardId, false);
    } else if (kindOf(c, library) === CardKind.TRAP) {
      actions.push({ type: ActionType.ACTIVATE_TRAP, cardUid: c.uid });
    }
  }
  if (z.fieldSpell && !z.fieldSpell.faceUp) {
    pushSpellActivations(
      actions, state, library, seat, z.fieldSpell.uid, z.fieldSpell.cardId, false,
    );
  }
}

function pushSpellActivations(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  cardUid: string,
  cardId: string,
  fromHand: boolean,
): void {
  const type = fromHand
    ? ActionType.ACTIVATE_SPELL_FROM_HAND
    : ActionType.ACTIVATE_SET_SPELL;

  // Fissure always destroys the lowest face-up ATK. With two or more public
  // bodies it snipes the chump and leaves the real threat — do not list it.
  if (cardId === SOUL_EXCHANGE) {
    const opp = getMonsters(state.zones[otherSeat(seat)]);
    for (const m of opp) actions.push(spellAction(type, cardUid, [m.uid]));
    return;
  }

  if (cardId === FLUTE) {
    pushFlute(actions, state, library, seat, type, cardUid);
    return;
  }

  if (cardId === GHOUL) {
    pushGhoul(actions, state, library, seat, type, cardUid);
    return;
  }

  if (cardId === DE_SPELL) {
    pushKnownBackrow(actions, state, library, seat, type, cardUid, CardKind.SPELL);
    return;
  }

  if (cardId === FISSURE) {
    const n = getMonsters(state.zones[otherSeat(seat)]).filter((m) => m.faceUp).length;
    if (n !== 1) return;
    actions.push(spellAction(type, cardUid));
    return;
  }

  if (cardId === POLYMERIZATION) {
    const fusions = extraFusionsByAtk(state, library, seat).slice(0, MAX_FUSION_TARGETS);
    if (fusions.length === 0) {
      actions.push(spellAction(type, cardUid));
      return;
    }
    for (const f of fusions) {
      actions.push(spellAction(type, cardUid, [f.uid], CardPosition.ATTACK));
    }
    return;
  }

  if (cardId === '83764718') {
    const gy = bothGyMonsters(state, library)
      .sort(
        (a, b) =>
          getModifiedAtk(b, library, state.reverseStatMods) -
          getModifiedAtk(a, library, state.reverseStatMods),
      )
      .slice(0, MAX_REBORN_TARGETS);
    if (gy.length === 0) {
      actions.push(spellAction(type, cardUid, undefined, CardPosition.ATTACK));
      return;
    }
    for (const m of gy) {
      const fake = {
        ...m,
        faceUp: true,
        position: CardPosition.ATTACK,
        attackedThisTurn: false,
      };
      const wantAtk = shouldMonsterBeInAttack(state, library, seat, fake);
      actions.push(spellAction(type, cardUid, [m.uid], CardPosition.ATTACK));
      if (!wantAtk) {
        actions.push(spellAction(type, cardUid, [m.uid], CardPosition.DEFENSE));
      }
    }
    return;
  }

  if (cardId === '4031928') {
    const opp = getMonsters(state.zones[otherSeat(seat)]);
    if (opp.length === 0) {
      actions.push(spellAction(type, cardUid));
      return;
    }
    for (const m of opp) {
      actions.push(spellAction(type, cardUid, [m.uid]));
    }
    return;
  }

  if (cardId === '63102017') {
    const defs = getMonsters(state.zones[otherSeat(seat)])
      .filter((m) => m.position === CardPosition.DEFENSE);
    if (defs.length === 0) {
      actions.push(spellAction(type, cardUid));
      return;
    }
    for (const m of defs) {
      actions.push(spellAction(type, cardUid, [m.uid]));
    }
    return;
  }

  if (Number(cardId) === SHIELD_AND_SWORD) {
    if (shieldSwordHelps(state, library, seat)) actions.push(spellAction(type, cardUid));
    return;
  }

  if (cardId === '19613556') {
    if (heavyStormWorthIt(state, library, seat)) actions.push(spellAction(type, cardUid));
    return;
  }

  if (cardId === '79759861') {
    if (doomedHelps(state, library, seat)) actions.push(spellAction(type, cardUid));
    return;
  }

  if (cardId === '41142615') {
    if (coffinHelps(state, library, seat)) actions.push(spellAction(type, cardUid));
    return;
  }

  if (cardId === '56830749') {
    if (shareThePainHelps(state, library, seat)) actions.push(spellAction(type, cardUid));
    return;
  }

  if (cardId === '25880422') {
    const attacks = getMonsters(state.zones[otherSeat(seat)]).filter(
      (monster) => monster.faceUp && monster.position === CardPosition.ATTACK,
    );
    for (const monster of attacks) actions.push(spellAction(type, cardUid, [monster.uid]));
    return;
  }

  const equip = getEquipConfig(Number(cardId));
  if (equip) {
    const hosts = ownEquipHosts(state, library, seat, equip.cardId)
      .sort(
        (a, b) =>
          getModifiedAtk(b, library, state.reverseStatMods) -
          getModifiedAtk(a, library, state.reverseStatMods),
      )
      .slice(0, 3);
    for (const h of hosts) {
      actions.push(spellAction(type, cardUid, [h.uid]));
    }
    return;
  }

  actions.push(spellAction(type, cardUid));
}

function pushFlute(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  type: ActionType.ACTIVATE_SPELL_FROM_HAND | ActionType.ACTIVATE_SET_SPELL,
  cardUid: string,
): void {
  const dragons = state.zones[seat].hand
    .filter((c) => kindOf(c, library) === CardKind.MONSTER && getCardData(library, c.cardId).race === 'Dragon')
    .sort(
      (a, b) =>
        getModifiedAtk(b, library, state.reverseStatMods) -
        getModifiedAtk(a, library, state.reverseStatMods),
    );
  const free = state.zones[seat].monsters.filter((m) => m === null).length;
  const cap = Math.min(2, free, dragons.length);
  if (cap <= 0) return;
  const picks: string[][] = [[dragons[0].uid]];
  if (cap >= 2) picks.push(dragons.slice(0, 2).map((c) => c.uid));
  if (dragons[1]) picks.push([dragons[1].uid]);
  const seen = new Set<string>();
  for (const uids of picks) {
    const key = uids.join(',');
    if (seen.has(key)) continue;
    seen.add(key);
    actions.push(spellAction(type, cardUid, uids));
  }
}

function pushGhoul(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  type: ActionType.ACTIVATE_SPELL_FROM_HAND | ActionType.ACTIVATE_SET_SPELL,
  cardUid: string,
): void {
  const gy = state.zones[otherSeat(seat)].graveyard
    .filter((c) => kindOf(c, library) === CardKind.MONSTER)
    .sort(
      (a, b) =>
        getModifiedAtk(b, library, state.reverseStatMods) -
        getModifiedAtk(a, library, state.reverseStatMods),
    );
  const holdingReborn = state.zones[seat].hand.some((c) => c.cardId === REBORN);
  const pool = holdingReborn ? gy.slice(1) : gy;
  if (pool.length === 0) return;
  actions.push(spellAction(type, cardUid, pool.slice(0, 2).map((c) => c.uid)));
}

function pushKnownBackrow(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  type: ActionType.ACTIVATE_SPELL_FROM_HAND | ActionType.ACTIVATE_SET_SPELL,
  cardUid: string,
  kind: CardKind,
): void {
  let pushed = 0;
  for (const owner of [0, 1] as Seat[]) {
    const field = state.zones[owner].fieldSpell;
    const cards = [...getSpellTraps(state.zones[owner]), ...(field ? [field] : [])];
    for (const card of cards) {
      if (card.uid === cardUid) continue;
      const id = card.faceUp ? card.cardId : knownCardId(state, seat, card.uid);
      if (!id) continue;
      if (kindOf({ ...card, cardId: id }, library) !== kind) continue;
      actions.push(spellAction(type, cardUid, [card.uid]));
      pushed += 1;
    }
  }
  if (pushed === 0) actions.push(spellAction(type, cardUid));
}

/** Net ATK the seat gains if printed ATK and DEF switch. Already-switched monsters stay. */
function shieldSwordHelps(state: MatchState, library: CardLibrary, seat: Seat): boolean {
  let swing = 0;
  let any = false;
  for (const side of [0, 1] as Seat[]) {
    for (const monster of getMonsters(state.zones[side])) {
      if (!monster.faceUp || monster.baseSwapped) continue;
      const data = getCardData(library, monster.cardId);
      const bonus = effectiveStatBonus(monster, state.reverseStatMods);
      const now = Math.max(0, (data.atk ?? 0) + bonus.atk);
      const next = Math.max(0, (data.def ?? 0) + bonus.atk);
      const delta = next - now;
      swing += side === seat ? delta : -delta;
      any = true;
    }
  }
  return any && swing > 0;
}

function offeringWorthPaying(state: MatchState, library: CardLibrary, seat: Seat): boolean {
  let best = 0;
  for (const card of state.zones[seat].hand) {
    if (kindOf(card, library) !== CardKind.MONSTER) continue;
    if (isExtraDeckCard(getCardData(library, card.cardId))) continue;
    if (getLevel(card, library) > 4) continue;
    best = Math.max(best, getModifiedAtk(card, library, state.reverseStatMods));
  }
  if (best <= 0) return false;
  const oppLp = state.zones[otherSeat(seat)].lifePoints;
  const swing = forecastCombat(state, library, seat);
  if (swing.attackerDamage + best >= oppLp && swing.attackerDamage < oppLp) return true;
  return best * 0.45 > 500;
}

function spellAction(
  type: ActionType.ACTIVATE_SPELL_FROM_HAND | ActionType.ACTIVATE_SET_SPELL,
  cardUid: string,
  targetUids?: string[],
  position?: CardPosition,
): PlayerAction {
  if (type === ActionType.ACTIVATE_SPELL_FROM_HAND) {
    if (targetUids && position) {
      return { type, cardUid, targetUids, position };
    }
    if (targetUids) return { type, cardUid, targetUids };
    if (position) return { type, cardUid, position };
    return { type, cardUid };
  }
  if (targetUids && position) {
    return { type, cardUid, targetUids, position };
  }
  if (targetUids) return { type, cardUid, targetUids };
  if (position) return { type, cardUid, position };
  return { type, cardUid };
}

function bothGyMonsters(state: MatchState, library: CardLibrary) {
  return [...state.zones[0].graveyard, ...state.zones[1].graveyard].filter(
    (c) => kindOf(c, library) === CardKind.MONSTER,
  );
}

function tributeFodderOptions(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  need: number,
): string[][] {
  const field = tributeField(state, seat);
  if (field.length < need) return [];
  const scored = field
    .map((m) => ({ uid: m.uid, keep: tributeKeepScore(m, state, library, seat) }))
    .sort((a, b) => a.keep - b.keep);
  const worst = scored.slice(0, need).map((s) => s.uid);
  const options = [worst];
  const killers = new Set(scored.filter((s) => s.keep >= 8000).map((s) => s.uid));
  if (killers.size > 0) {
    const spare = scored.filter((s) => !killers.has(s.uid));
    if (spare.length >= need) {
      options.push(spare.slice(0, need).map((s) => s.uid));
    }
  }
  const seen = new Set<string>();
  return options.filter((uids) => {
    const key = [...uids].sort().join(',');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, 3);
}

function tributeField(state: MatchState, seat: Seat): CardInstance[] {
  const field = [...getMonsters(state.zones[seat])];
  const marks = new Set(soulExchangeTributeUids(state, seat));
  if (marks.size === 0) return field;
  for (const monster of getMonsters(state.zones[otherSeat(seat)])) {
    if (marks.has(monster.uid)) field.push(monster);
  }
  return field;
}

/**
 * How much we want this body to stay. Unique killers outrank generic beaters;
 * fodder that cannot win a public fight is tributed first.
 */
function tributeKeepScore(
  monster: CardInstance,
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): number {
  if (monster.owner !== seat) return 0;
  const reverse = state.reverseStatMods;
  const atk = getModifiedAtk(monster, library, reverse);
  const teammates = getMonsters(state.zones[seat]).filter((m) => m.uid !== monster.uid);
  const oppFaceUp = getMonsters(state.zones[otherSeat(seat)]).filter((m) => m.faceUp);
  let kills = 0;
  let uniqueKills = 0;
  for (const o of oppFaceUp) {
    const bar = o.position === CardPosition.ATTACK
      ? getModifiedAtk(o, library, reverse)
      : getModifiedDef(o, library, reverse);
    if (atk <= bar) continue;
    kills += 1;
    const otherCan = teammates.some((t) => {
      const tAtk = getModifiedAtk(t, library, reverse);
      return tAtk > bar;
    });
    if (!otherCan) uniqueKills += 1;
  }
  return uniqueKills * 8000 + kills * 2000 + atk;
}

function doomedHelps(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  const easy = plannerDifficulty(state, seat) === 'easy';
  let victim = false;
  for (const side of [0, 1] as Seat[]) {
    for (const monster of getMonsters(state.zones[side])) {
      if (!monster.faceUp && !easy) continue;
      const atk = getModifiedAtk(monster, library, state.reverseStatMods);
      if (easy || atk >= 1500) victim = true;
    }
  }
  if (!victim) return false;
  const counts = new Map<string, number>();
  for (const card of state.zones[seat].hand) {
    counts.set(card.cardId, (counts.get(card.cardId) ?? 0) + 1);
  }
  const lastCopy = new Set([
    '55144522', '12580477', '53129443', '83764718', '4031928',
    '44095762', '26202165', '78010363', '40640057', '31560081',
  ]);
  return state.zones[seat].hand.some((card) => {
    if (card.cardId === '79759861' && (counts.get(card.cardId) ?? 0) <= 1) return false;
    if (EXODIA_PIECE_SET.has(card.cardId)) return false;
    if (lastCopy.has(card.cardId) && (counts.get(card.cardId) ?? 0) <= 1) return false;
    return true;
  });
}

function coffinHelps(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  const ghoul = '30778711';
  const hasGhoul = state.zones[seat].hand.some((card) => card.cardId === ghoul)
    || getMonsters(state.zones[seat]).some((card) => card.cardId === ghoul);
  if (!hasGhoul) return false;
  const keep = new Set(['26202165', '78010363', '40640057', '31560081']);
  return state.zones[seat].hand.some((card) => {
    if (kindOf(card, library) !== CardKind.MONSTER) return false;
    if (EXODIA_PIECE_SET.has(card.cardId) || keep.has(card.cardId)) return false;
    return (getCardData(library, card.cardId).atk ?? 0) <= 1000;
  });
}

function shareThePainHelps(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): boolean {
  const recruiters = new Set([26202165, 78010363]);
  const have = getMonsters(state.zones[seat]).some(
    (monster) => recruiters.has(Number(monster.cardId)),
  );
  if (!have) return false;
  return getMonsters(state.zones[otherSeat(seat)]).some(
    (monster) => getModifiedAtk(monster, library, state.reverseStatMods) >= 1500,
  );
}

/**
 * A lost coin destroys our monsters. Normal+ only calls Time Wizard when it
 * is the only body, or when a correct call leaves a lethal swing. Easy still
 * calls whenever the opponent is ahead.
 */
function timeWizardOffer(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  mine: CardInstance[],
  oppFace: CardInstance[],
  oppAtk: number,
  ourAtk: number,
): boolean {
  const level = plannerDifficulty(state, seat);
  const alone = mine.length === 1;
  if (level === 'easy' || level === 'test') {
    return oppAtk > ourAtk || (oppFace.length > 0 && alone);
  }
  if (getMonsters(state.zones[otherSeat(seat)]).length === 0) return false;
  if (alone) return true;
  const swing = mine.reduce((sum, monster) => {
    if (!monster.faceUp) return sum;
    return sum + getModifiedAtk(monster, library, state.reverseStatMods);
  }, 0);
  return swing >= state.zones[otherSeat(seat)].lifePoints;
}

function pushMonsterIgnitions(
  actions: PlayerAction[],
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
): void {
  const zone = state.zones[seat];
  const opp = otherSeat(seat);
  for (const card of zone.hand) {
    if (Number(card.cardId) !== 31786629) continue;
    const copies = zone.deck.filter((c) => Number(c.cardId) === 31786629).length;
    if (copies > 0) actions.push({ type: ActionType.ACTIVATE_MONSTER_EFFECT, cardUid: card.uid });
  }
  const mine = getMonsters(zone).filter((monster) => monster.faceUp);
  const oppFace = getMonsters(state.zones[opp]).filter((monster) => monster.faceUp);
  const oppAtk = oppFace.reduce(
    (sum, monster) => sum + getModifiedAtk(monster, library, state.reverseStatMods),
    0,
  );
  const ourAtk = mine.reduce(
    (sum, monster) => sum + getModifiedAtk(monster, library, state.reverseStatMods),
    0,
  );
  for (const monster of mine) {
    if (monster.ignitionUsedThisTurn) continue;
    const id = Number(monster.cardId);
    if (id === 11384280) {
      const recruiter = mine.some((other) => {
        const otherId = Number(other.cardId);
        return other.uid !== monster.uid && (otherId === 26202165 || otherId === 78010363);
      });
      const lethal = state.zones[opp].lifePoints <= 500;
      // Main 1 keeps the stolen monster for the attack. Main 2 tributes it
      // so Change of Heart does not hand it back.
      const borrowed =
        state.phase === EnginePhase.MAIN_2 &&
        mine.some(
          (other) => other.uid !== monster.uid && other.controlReturnTo !== undefined,
        );
      if (recruiter || lethal || borrowed) {
        actions.push({ type: ActionType.ACTIVATE_MONSTER_EFFECT, cardUid: monster.uid });
      }
    }
    if (id === 95727991) {
      const best = mine.reduce(
        (max, body) => Math.max(max, getModifiedAtk(body, library, state.reverseStatMods)),
        0,
      );
      if (best >= 2400 || Math.floor(best / 2) >= state.zones[opp].lifePoints) {
        actions.push({ type: ActionType.ACTIVATE_MONSTER_EFFECT, cardUid: monster.uid });
      }
    }
    if (id === 71625222 && timeWizardOffer(state, library, seat, mine, oppFace, oppAtk, ourAtk)) {
      actions.push({ type: ActionType.ACTIVATE_MONSTER_EFFECT, cardUid: monster.uid });
    }
    if (id === 81480460 && oppFace.some(
      (body) => getModifiedAtk(body, library, state.reverseStatMods) >= 1500,
    )) {
      actions.push({ type: ActionType.ACTIVATE_MONSTER_EFFECT, cardUid: monster.uid });
    }
  }
}

function ownEquipHosts(
  state: MatchState,
  library: CardLibrary,
  seat: Seat,
  equipCardId: number,
) {
  const cfg = getEquipConfig(equipCardId);
  if (!cfg) return [];
  const lockOpponent = equipCardId === 50152549;
  const harmful = (cfg.standbyAtk ?? 0) < 0 && cfg.atkBoost <= 0 && cfg.defBoost <= 0;
  const seats: Seat[] = lockOpponent || (!cfg.controllerOnly && harmful)
    ? [otherSeat(seat)]
    : [seat];
  return seats.flatMap((side) => getMonsters(state.zones[side])).filter((m) => {
    if (!m.faceUp) return false;
    const data = getCardData(library, m.cardId);
    if (cfg.raceFilter && data.race !== cfg.raceFilter) return false;
    if (cfg.excludeRace && data.race === cfg.excludeRace) return false;
    if (
      cfg.attributeFilter &&
      data.attribute?.toUpperCase() !== cfg.attributeFilter
    ) {
      return false;
    }
    return true;
  });
}

function enumerateBattle(state: MatchState, seat: Seat): PlayerAction[] {
  if (isOpeningTurn(state)) return [ADVANCE];
  if (
    state.pendingAttack ||
    state.chain.length > 0 ||
    (state.battleStep && state.battleStep !== 'BATTLE')
  ) {
    return [ADVANCE];
  }
  const z = state.zones[seat];
  if (z.attackLockTurns > 0) return [ADVANCE];

  const attackers = getMonsters(z).filter(
    (m) =>
      m.faceUp &&
      m.position === CardPosition.ATTACK &&
      !m.attackedThisTurn &&
      !attackLockCode(state, seat, m),
  );
  const opp = otherSeat(seat);
  const forced = magnetismTargets(state, opp);
  const oppMonsters = forced.length > 0 ? forced : getMonsters(state.zones[opp]);
  const actions: PlayerAction[] = [];

  for (const attacker of attackers) {
    if (oppMonsters.length === 0) {
      actions.push({ type: ActionType.DIRECT_ATTACK, attackerUid: attacker.uid });
      continue;
    }
    for (const t of oppMonsters) {
      actions.push({
        type: ActionType.DECLARE_ATTACK,
        attackerUid: attacker.uid,
        targetUid: t.uid,
      });
    }
  }
  actions.push(ADVANCE);
  return actions;
}
